import { use } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { z } from "zod";
import { AuthUIContext } from "@papier/auth-ui";
import { authClient } from "@/lib/auth-client";
import { useSearchFilters } from "@/hooks/use-search-filters";
import { DomainsCard } from "@/components/sending/domains-card";
import { ConnectionsCard } from "@/components/sending/connections-card";
import { SuppressionsCard } from "@/components/sending/suppressions-card";
import { PendingApiKeysComponent } from "@/components/pending";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@papier/ui/components/ui/tabs";
import { Button } from "@papier/ui/components/ui/button";
import {
  ArrowLeft,
  PlugCircle as Plug,
  ShieldCheck,
  UserCrossRounded as UserRoundX,
} from "@papier/ui/components/icons";
import { OrganizationPageHeader } from "@/components/orgs/organization-page";

export const Route = createFileRoute("/_app/organization/sending")({
  component: ResourcesPage,
  validateSearch: z.object({
    section: z.enum(["connections", "domains", "suppressions"]).default("connections"),
    returnProject: z.string().min(1).max(255).optional(),
    returnEnv: z.string().max(255).optional(),
  }),
  head: () => ({ meta: [{ title: "Papier - Sending resources" }] }),
});

function ResourcesPage() {
  const { filters, setFilters } = useSearchFilters(Route.id);
  const {
    hooks: { useHasPermission },
  } = use(AuthUIContext);
  const { data: organization, isPending } = authClient.useActiveOrganization();
  const { data: permission, isPending: permissionPending } = useHasPermission({
    organizationId: organization?.id,
    permissions: { organization: ["update"] },
  });
  if (isPending || permissionPending) return <PendingApiKeysComponent />;
  const canManage = permission?.success === true;
  return (
    <main key={organization?.id} className="w-full min-w-0 space-y-7">
      {filters.returnProject && (
        <Button variant="ghost" size="sm" asChild>
          <Link
            to="/projects/$projectSlug/sending"
            params={{ projectSlug: filters.returnProject }}
            search={{ env: filters.returnEnv }}
          >
            <ArrowLeft className="size-3.5" />
            Back to project sending
          </Link>
        </Button>
      )}
      <OrganizationPageHeader
        title="Sending resources"
        description="Manage provider accounts, sender domains, and blocked recipients shared across all projects in this organization."
      />
      <p className="rounded-xl border border-primary/10 bg-primary/4 p-4 text-xs leading-relaxed text-muted-foreground">
        These resources are shared. Choose how each channel sends messages in a project's Sending
        page
        {filters.returnProject ? (
          <>
            {" "}
            (
            <Link
              to="/projects/$projectSlug/sending"
              params={{ projectSlug: filters.returnProject }}
              search={{ env: filters.returnEnv }}
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              open {filters.returnProject}
            </Link>
            )
          </>
        ) : (
          <>
            {" "}
            from{" "}
            <Link
              to="/projects"
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              your projects
            </Link>
          </>
        )}
        .
      </p>
      <Tabs
        value={filters.section}
        onValueChange={(value) => {
          const section = ["connections", "domains", "suppressions"].find(
            (entry) => entry === value,
          );
          if (section === "connections" || section === "domains" || section === "suppressions")
            void setFilters({ section });
        }}
        className="gap-5"
      >
        <TabsList
          aria-label="Sending resources"
          className="grid h-auto w-full grid-cols-3 gap-1 rounded-xl border bg-muted/25 p-1 backdrop-blur-none sm:p-1.5"
        >
          {[
            {
              value: "connections",
              label: "Connections",
              description: "Your provider accounts",
              icon: Plug,
            },
            {
              value: "domains",
              label: "Sending domains",
              description: "Verify email senders",
              icon: ShieldCheck,
            },
            {
              value: "suppressions",
              label: "Blocked recipients",
              description: "Manage sending exclusions",
              icon: UserRoundX,
            },
          ].map((item) => (
            <TabsTrigger
              key={item.value}
              value={item.value}
              aria-label={item.label}
              className="h-auto min-w-0 flex-col gap-1 rounded-lg px-1 py-2 text-center whitespace-normal data-[state=active]:border-border/70 data-[state=active]:bg-background data-[state=active]:shadow-xs data-[state=active]:backdrop-blur-none sm:flex-row sm:gap-3 sm:px-3 sm:py-3 sm:text-left dark:data-[state=active]:bg-background"
            >
              <item.icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="min-w-0">
                <span className="block text-xs font-medium sm:text-sm">{item.label}</span>
                <span className="mt-1 hidden text-[11px] font-normal text-muted-foreground sm:block">
                  {item.description}
                </span>
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="connections">
          {canManage ? (
            <ConnectionsCard canManage />
          ) : (
            <p className="rounded-xl border bg-background p-6 text-sm text-muted-foreground">
              An organization administrator manages provider connections.
            </p>
          )}
        </TabsContent>
        <TabsContent value="domains">
          <DomainsCard canManage={canManage} />
        </TabsContent>
        <TabsContent value="suppressions">
          <SuppressionsCard canManage={canManage} />
        </TabsContent>
      </Tabs>
    </main>
  );
}
